import { ref, onBeforeUnmount } from "vue";
import type { ActiveLedgerResponse } from "~/types/payments";

/**
 * Polls the active-ledger endpoint (refund variant) until the in-flight
 * refund batch resolves. Mirrors the Nuxt 2 `refundBatchPolling` mixin:
 * 12 attempts at 1.5 s = 18 s budget, token-cancellable so a component
 * unmount or a "cancel" click stops the loop without racing the modal
 * state machine.
 *
 * Uses `/payments/active-ledger/{order_id}/refund` (the lighter ledger-
 * lookup endpoint) rather than the heavier `active-transaction` view —
 * polling at 1.5 s intervals doesn't need a live Stripe PI fetch, and
 * the active-ledger response carries the same `batch` aggregation we
 * gate on.
 *
 * Resolution comes from `batch.all_confirmed` / `batch.any_failed` —
 * never the per-ledger payment_status, because a multi-chunk refund's
 * individual ledgers settle out of order and we defer all UI side-
 * effects until the batch as a whole is done.
 */

const DEFAULT_MAX_ATTEMPTS = 12;
const DEFAULT_DELAY_MS = 1500;

export type RefundPollResult = "success" | "failed" | "timeout" | "cancelled";

export interface RefundPollOptions {
  orderId: string | number;
  maxAttempts?: number;
  delayMs?: number;
}

export function useRefundBatchPolling() {
  const { authFetch } = useAuthenticatedFetch();
  // Monotonic token: each new poll bumps it. An in-flight loop bails as
  // soon as it observes its token != current.
  const pollToken = ref(0);

  async function pollRefundBatchConfirmation(
    opts: RefundPollOptions,
  ): Promise<RefundPollResult> {
    const myToken = ++pollToken.value;
    const maxAttempts = opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    const delayMs = opts.delayMs ?? DEFAULT_DELAY_MS;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (myToken !== pollToken.value) return "cancelled";

      let res: ActiveLedgerResponse;
      try {
        res = await authFetch<ActiveLedgerResponse>(
          `/api/customers/orders/payments/active-ledger/${opts.orderId}/refund`,
        );
      } catch {
        // Transient fetch error — wait and retry within the same budget.
        await wait(delayMs);
        continue;
      }

      if (myToken !== pollToken.value) return "cancelled";

      const batch = res.batch;
      // No batch (no active refund ledger or server cleared it) → the
      // refund has already settled and dropped out of the active view.
      // Treat as success.
      if (!batch) return "success";

      if (batch.any_failed) return "failed";
      if (batch.all_confirmed) return "success";

      await wait(delayMs);
    }

    return "timeout";
  }

  function cancelRefundBatchPolling(): void {
    pollToken.value++;
  }

  // Auto-cancel on component unmount so a navigation mid-poll doesn't
  // leave a zombie loop running against the API.
  onBeforeUnmount(() => {
    cancelRefundBatchPolling();
  });

  return { pollRefundBatchConfirmation, cancelRefundBatchPolling };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
